import { usePermissions } from '@my/directory-api/hooks';
import { useOrderDispatches } from '@my/tms-api/hooks';
import { cn } from '@my/ui';
import { Send } from 'lucide-react';

/**
 * Driver jobs — the Actions-row button that opens the panel. Its badge is the
 * thing worth noticing: new driver messages (amber), or a live job the order
 * has changed under (an amber dot — send an update). Only with TMS Mobile, and
 * only on a saved order.
 */
export function DispatchButton({
  orderId,
  organisationId,
  onOpen,
}: {
  orderId: string | null;
  organisationId: string | null;
  onOpen: () => void;
}) {
  const { can } = usePermissions();
  const enabled = can('mobile') && !!orderId;
  const query = useOrderDispatches(enabled ? organisationId : null, enabled ? orderId : null);
  if (!enabled) return null;

  const live = (query.data?.dispatches ?? []).filter((d) => ['Sent', 'Accepted', 'Queried'].includes(d.status));
  const unread = live.reduce((n, d) => n + d.unreadMessages, 0);
  const changed = live.some((d) => d.changedSinceSent);

  const title = [
    'Driver jobs',
    live.length ? `${live.length} on drivers’ phones` : 'nothing sent yet',
    unread ? `${unread} new message${unread === 1 ? '' : 's'}` : null,
    changed ? 'the order has changed since a job was sent' : null,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <button
      type="button"
      onClick={onOpen}
      title={title}
      aria-label="Driver jobs"
      className="relative inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
    >
      <Send className="h-3.5 w-3.5" />
      {unread > 0 ? (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-semibold text-white ring-2 ring-white">
          {unread}
        </span>
      ) : (
        (changed || live.length > 0) && (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-white',
              changed ? 'bg-amber-500' : 'bg-emerald-500',
            )}
          />
        )
      )}
    </button>
  );
}
